import { useState, useEffect, useRef } from 'react'
import {
	Typography,
	Button,
	Box,
	IconButton,
	useMediaQuery,
	useTheme,
	Popover,
	Drawer,
	Snackbar,
	Alert,
	CircularProgress
} from '@mui/material'
import {
	ArrowBack as ArrowBackIcon,
	Add as AddIcon
} from '@mui/icons-material'
import { supabase } from "../utils/supabase"
import { getFullName } from '../utils/clientUtils'
import { uploadPetAvatar, deleteAllPetPhotos } from '../utils/petPhotoUtils'
import AddEditPetPage from './AddEditPetPage'
import AddAppointmentDialog from './AddAppointmentDialog'
import AddEditClientDialog from './AddEditClientDialog'
import PetInfoCard from './PetInfoCard'
import { ReactComponent as CalendarIcon } from '../assets/appointment-icons/calendar.svg'
import { ReactComponent as MessageIcon } from '../assets/appointment-icons/message.svg'
import { ReactComponent as SendIcon } from '../assets/appointment-icons/send.svg'
import { ReactComponent as UserIcon } from '../assets/appointment-icons/user.svg'
import dayjs from 'dayjs'
import utc from 'dayjs/plugin/utc'
import timezone from 'dayjs/plugin/timezone'

dayjs.extend(utc)
dayjs.extend(timezone)

// Nami lavender theme tokens (from the design handoff) — mobile "Client Details" screen
const CMD = {
	page: '#FBFAFE',
	surface: '#F5F3FB',
	border: '#E7E2F3',
	field: '#FFFFFF',
	text: '#2E2A40',
	muted: '#8B85A4',
	primary: '#7C6BD9',
	primaryDeep: '#5F4EC2',
	primarySoft: '#EDE9FB'
}
const FONTD = "'Outfit', sans-serif"

/**
 * Add-ons on the next-appointment card, one per line (design `.up-pet-addons`).
 * Reads the appointment's add_ons JSON column ([{ service_id, name, price,
 * duration_minutes }]); renders nothing when the appointment has none.
 */
function NextApptAddOns({ appointment }) {
	const addOns = Array.isArray(appointment?.add_ons) ? appointment.add_ons : []
	if (addOns.length === 0) return null
	return addOns.map((a, i) => (
		<Typography
			key={a.service_id ?? i}
			sx={{
				fontFamily: FONTD, fontSize: '11.5px', fontWeight: 500, color: CMD.muted, mt: '2px',
				whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
			}}
		>
			+ {a.name}
		</Typography>
	))
}

function ClientDetailsDialog({ onClose, client, clientPets, merchant, onMessageClient, onClientUpdated, onPetsUpdated, onPetAvatarUpdated, focusPetId }) {
	const theme = useTheme()
	const isMobile = useMediaQuery(theme.breakpoints.down('md'))
	const [activeTab, setActiveTab] = useState('information')
	const [totalBookings, setTotalBookings] = useState(0)
	const [completedBookings, setCompletedBookings] = useState(0)
	const [nextAppointment, setNextAppointment] = useState(null)
	const [pets, setPets] = useState(clientPets || [])
	// When arriving from an appointment's pet card, bring that pet into view
	// and ring it briefly so it's obvious which one was tapped.
	const focusRef = useRef(null)
	const [ringPet, setRingPet] = useState(null)
	const [editingPet, setEditingPet] = useState(null)
	const [isAddingPet, setIsAddingPet] = useState(false)
	const [appointmentDialogOpen, setAppointmentDialogOpen] = useState(false)
	const [editClientDialogOpen, setEditClientDialogOpen] = useState(false)
	const [currentClient, setCurrentClient] = useState(client)

	// Send agreements state
	const [sendAgreementsAnchor, setSendAgreementsAnchor] = useState(null)
	const [agreements, setAgreements] = useState([])
	const [selectedAgreementIds, setSelectedAgreementIds] = useState([])
	const [sendingAgreement, setSendingAgreement] = useState(false)
	const [sendSuccessOpen, setSendSuccessOpen] = useState(false)

	useEffect(() => {
		if (client?.id && merchant?.id) {
			loadBookingStats()
			loadNextAppointment()
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [client?.id, merchant?.id])

	useEffect(() => {
		if (merchant?.id) {
			supabase
				.from('agreements')
				.select('id, title')
				.eq('merchant_id', merchant.id)
				.is('deleted_at', null)
				.then(({ data }) => setAgreements(data || []))
		}
	}, [merchant?.id])

	useEffect(() => {
		setPets(clientPets || [])
	}, [clientPets])

	useEffect(() => {
		if (!focusPetId || !pets.some(p => p.id === focusPetId)) return
		setRingPet(focusPetId)
		const raf = requestAnimationFrame(() => {
			focusRef.current?.scrollIntoView({ block: 'center', behavior: 'smooth' })
		})
		const t = setTimeout(() => setRingPet(null), 2200)
		return () => { cancelAnimationFrame(raf); clearTimeout(t) }
	}, [focusPetId, pets])

	useEffect(() => {
		setCurrentClient(client)
	}, [client])

	const loadBookingStats = async () => {
		try {
			// Get total bookings
			const { count: total } = await supabase
				.from('appointments')
				.select('*', { count: 'exact', head: true })
				.eq('user_id', client.id)
				.eq('merchant_id', merchant.id)
				.not('status', 'eq', 'cancelled')

			setTotalBookings(total || 0)

			// Get completed bookings
			const { count: completed } = await supabase
				.from('appointments')
				.select('*', { count: 'exact', head: true })
				.eq('user_id', client.id)
				.eq('merchant_id', merchant.id)
				.eq('status', 'completed')

			setCompletedBookings(completed || 0)
		} catch (error) {
			console.error('Error loading booking stats:', error)
		}
	}

	const loadNextAppointment = async () => {
		try {
			const { data, error } = await supabase
				.from('appointments')
				.select(`
					id,
					start_ts,
					end_ts,
					service_id,
					employee_id,
					pet_id,
					add_ons,
					service_types(name),
					employees(first_name, last_name),
					pets(name)
				`)
				.eq('user_id', client.id)
				.eq('merchant_id', merchant.id)
				.in('status', ['booked'])
				.gte('start_ts', new Date().toISOString())
				.order('start_ts', { ascending: true })
				.limit(1)
				.single()

			if (!error && data) {
				setNextAppointment(data)
			}
		} catch (error) {
			console.error('Error loading next appointment:', error)
		}
	}

	if (!currentClient) {
		return null
	}

	const isNewClient = currentClient.created_at && dayjs().diff(dayjs(currentClient.created_at), 'day') < 30

	const formatClientSince = (dateString) => {
		if (!dateString) return ''
		return dayjs(dateString).format('MMM DD, YYYY')
	}

	const formatAppointmentTime = (startTs, endTs) => {
		const start = dayjs(startTs).tz(merchant?.timezone || 'UTC')
		const end = dayjs(endTs).tz(merchant?.timezone || 'UTC')
		return `${start.format('h:mm')} – ${end.format('h:mm A')}`
	}

	const handleAddPet = () => {
		setEditingPet(null)
		setIsAddingPet(true)
	}

	const handleEditPet = (pet) => {
		setEditingPet(pet)
		setIsAddingPet(true)
	}

	const handleClosePetForm = () => {
		setEditingPet(null)
		setIsAddingPet(false)
	}

	const handleSavePet = async (petData) => {
		try {
			// Extract pending avatar blob if present
			const pendingAvatarBlob = petData._pendingAvatarBlob
			delete petData._pendingAvatarBlob

			let savedPet = null

			if (editingPet) {
				// Update existing pet
				const { data, error } = await supabase
					.from('pets')
					.update(petData)
					.eq('id', editingPet.id)
					.select()
					.single()

				if (error) throw error
				savedPet = data

				// Update local state
				setPets(prev => prev.map(p => p.id === editingPet.id ? data : p))
			} else {
				// Create new pet
				const { data, error } = await supabase
					.from('pets')
					.insert({
						...petData,
						client_id: client.id
					})
					.select()
					.single()

				if (error) throw error
				savedPet = data

				// Update local state
				setPets(prev => [...prev, data])
			}

			// Upload pending avatar if present
			if (pendingAvatarBlob && savedPet && merchant?.id) {
				try {
					await uploadPetAvatar(
						savedPet.id,
						merchant.id,
						client.id,
						pendingAvatarBlob,
						null // No existing photo for new uploads
					)
				} catch (avatarError) {
					console.error('Error uploading avatar:', avatarError)
					// Don't fail the save if avatar upload fails
				}
			}

			// Notify parent of updated pets list
			const updatedPets = editingPet
				? pets.map(p => p.id === editingPet.id ? savedPet : p)
				: [...pets, savedPet]
			if (onPetsUpdated) {
				onPetsUpdated(client.id, updatedPets)
			}

			handleClosePetForm()
		} catch (error) {
			console.error('Error saving pet:', error)
			alert('Failed to save pet')
		}
	}

	// PetAvatar uploads immediately for an existing pet, so tell the parent right
	// away — otherwise its cached avatar map keeps the old photo until a reload.
	const handleAvatarUploaded = (result) => {
		if (editingPet?.id && onPetAvatarUpdated) {
			onPetAvatarUpdated(editingPet.id, result?.url || null)
		}
	}

	const handleDeletePet = async (petToDelete) => {
		// Use the passed pet or fall back to editingPet (for backward compatibility)
		const pet = petToDelete || editingPet
		if (!pet) return

		try {
			// Delete all pet photos from storage and database first
			await deleteAllPetPhotos(pet.id)

			const { error } = await supabase
				.from('pets')
				.delete()
				.eq('id', pet.id)

			if (error) throw error

			// Update local state
			const updatedPets = pets.filter(p => p.id !== pet.id)
			setPets(updatedPets)

			// Notify parent of updated pets list
			if (onPetsUpdated) {
				onPetsUpdated(client.id, updatedPets)
			}

			// Close the pet form if we're currently editing this pet
			if (editingPet && editingPet.id === pet.id) {
				handleClosePetForm()
			}
		} catch (error) {
			console.error('Error deleting pet:', error)
			alert('Failed to delete pet')
		}
	}

	const handleBookAppointment = () => {
		setAppointmentDialogOpen(true)
	}

	const handleAppointmentCreated = (appointment) => {
		// Optionally handle the created appointment (e.g., show success message, reload appointments)
		console.log('Appointment created:', appointment)
		// Reload next appointment
		loadNextAppointment()
	}

	const handleMessageClick = () => {
		if (onMessageClient) {
			onMessageClient(currentClient)
		}
	}

	const handleSendAgreementsClick = (event) => {
		setSelectedAgreementIds([])
		setSendAgreementsAnchor(event.currentTarget)
	}

	const handleSendAgreementsClose = () => {
		setSendAgreementsAnchor(null)
	}

	const toggleAgreementSelection = (id) => {
		setSelectedAgreementIds(prev =>
			prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]
		)
	}

	const handleSendAgreement = async () => {
		if (!selectedAgreementIds.length || sendingAgreement) return
		setSendingAgreement(true)
		try {
			for (const agreementId of selectedAgreementIds) {
				await supabase.functions.invoke('send-agreement', {
					body: {
						agreementId,
						clientId: currentClient.id,
						merchantId: merchant.id,
						channel: 'email'
					}
				})
			}
			setSendAgreementsAnchor(null)
			setSendSuccessOpen(true)
		} catch (err) {
			console.error('Failed to send agreement:', err)
		} finally {
			setSendingAgreement(false)
		}
	}

	const handleEditClient = () => {
		setEditClientDialogOpen(true)
	}

	const handleClientSaved = ({ client: updatedClient }) => {
		// Update local state with the new client data
		setCurrentClient(updatedClient)
		setEditClientDialogOpen(false)
		// Notify parent to refresh the client in their list
		if (onClientUpdated) {
			onClientUpdated(updatedClient)
		}
	}

	// Desktop version
	if (!isMobile) {
		return (
			<Box sx={{
				display: 'flex',
				flexDirection: 'column',
				height: '100%',
				overflow: 'hidden',
				padding: '18px 26px 22px 26px',
				bgcolor: CMD.page,
				boxSizing: 'border-box'
			}}>
				{/* Page Header (hidden while editing/adding a pet — the pet form has its own) */}
				{!(isAddingPet || editingPet) && (
				<Box sx={{
					display: 'flex',
					alignItems: 'center',
					gap: '10px',
					paddingTop: '8px',
					paddingBottom: '8px'
				}}>
					<IconButton
						onClick={onClose}
						sx={{
							width: 16,
							height: 16,
							p: 0,
							color: '#2D3748',
							'&:hover': {
								bgcolor: 'transparent',
								opacity: 0.7
							}
						}}
					>
						<ArrowBackIcon sx={{ fontSize: 16 }} />
					</IconButton>

					<Typography sx={{
						fontFamily: 'Inter',
						fontWeight: 600,
						fontSize: '28px',
						lineHeight: '1.286em',
						color: '#2D3748'
					}}>
						Client Details
					</Typography>

					<Box sx={{ flexGrow: 1 }} />

					{/* Add pet — top right (hidden while editing/adding a pet) */}
					{!(isAddingPet || editingPet) && (
						<Button
							onClick={handleAddPet}
							startIcon={<AddIcon sx={{ fontSize: 16 }} />}
							sx={{ height: '40px', px: '16px', border: 'none', borderRadius: '10px', bgcolor: '#7C6BD9', color: '#FFFFFF', textTransform: 'none', fontFamily: "'Outfit', sans-serif", fontWeight: 700, fontSize: '13.5px', whiteSpace: 'nowrap', boxShadow: '0 2px 8px #EDE9FB', '&:hover': { bgcolor: '#5F4EC2' }, '& .MuiButton-startIcon': { marginRight: '7px' } }}
						>
							Add pet
						</Button>
					)}
				</Box>
				)}

				{/* Main Content */}
				<Box sx={{
					display: 'flex',
					gap: '16px',
					paddingTop: '8px',
					paddingBottom: '8px',
					overflow: 'auto',
					flex: 1
				}}>
					{/* Left Section - Client Info (hidden while editing/adding a pet) */}
					{!(isAddingPet || editingPet) && (
						<Box sx={{
							display: 'flex',
							flexDirection: 'column',
							alignSelf: 'stretch',
							gap: '16px',
							padding: '18px',
							bgcolor: '#FFFFFF',
							border: '1px solid #E7E2F3',
							borderRadius: '14px',
							boxShadow: '0 1px 3px rgba(46, 42, 64, 0.04)',
							width: '300px',
							flexShrink: 0,
							boxSizing: 'border-box'
						}}>
						{/* Identity */}
						<Box sx={{ minWidth: 0 }}>
							<Box sx={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
								<Typography sx={{ fontFamily: "'Outfit', sans-serif", fontSize: '17px', fontWeight: 800, color: '#2E2A40', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
									{getFullName(currentClient)}
								</Typography>
								{isNewClient && (
									<Box sx={{ flexShrink: 0, fontFamily: "'Outfit', sans-serif", fontSize: '10px', fontWeight: 700, letterSpacing: '0.04em', textTransform: 'uppercase', color: '#5F4EC2', bgcolor: '#EDE9FB', padding: '2px 7px', borderRadius: '99px' }}>New</Box>
								)}
								<IconButton
									onClick={handleEditClient}
									aria-label="Edit client"
									sx={{ ml: 'auto', width: 26, height: 26, border: '1px solid #E7E2F3', borderRadius: '7px', bgcolor: '#FFFFFF', color: '#8B85A4', '&:hover': { color: '#5F4EC2', borderColor: '#7C6BD9' } }}
								>
									<svg width="13" height="13" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" xmlns="http://www.w3.org/2000/svg"><path d="M11.5 2.1a1.6 1.6 0 012.3 2.3l-8.2 8.2L2.2 13.8l1.2-3.4 8.1-8.3z" /></svg>
								</IconButton>
							</Box>
							<Typography sx={{ fontFamily: "'Outfit', sans-serif", fontSize: '12px', fontWeight: 600, color: '#8B85A4', mt: '3px' }}>
								Client since {formatClientSince(currentClient.created_at)}
							</Typography>
						</Box>

						{/* Stats */}
						<Box sx={{ display: 'flex', alignItems: 'center', bgcolor: '#F5F3FB', borderRadius: '10px', padding: '10px 4px' }}>
						{[
							{ num: totalBookings, label: 'Total bookings' },
							{ num: completedBookings, label: 'Completed' }
						].map((s, i) => (
							<Box key={s.label} sx={{ flex: 1, display: 'flex', alignItems: 'baseline', justifyContent: 'center', gap: '6px', borderLeft: i === 1 ? '1px solid #E7E2F3' : 'none' }}>
								<Typography sx={{ fontFamily: "'Outfit', sans-serif", fontSize: '17px', fontWeight: 800, color: '#2E2A40', letterSpacing: '-0.02em' }}>{s.num}</Typography>
								<Typography sx={{ fontFamily: "'Outfit', sans-serif", fontSize: '12.5px', fontWeight: 600, color: '#8B85A4', whiteSpace: 'nowrap' }}>{s.label}</Typography>
							</Box>
						))}
						</Box>

						{/* Actions */}
						<Box sx={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
							<Button
								onClick={handleBookAppointment}
								startIcon={<CalendarIcon width={15} height={15} />}
								sx={{ height: '40px', border: 'none', borderRadius: '10px', bgcolor: '#7C6BD9', color: '#FFFFFF', textTransform: 'none', fontFamily: "'Outfit', sans-serif", fontWeight: 700, fontSize: '13.5px', boxShadow: '0 2px 8px #EDE9FB', '&:hover': { bgcolor: '#5F4EC2' }, '& .MuiButton-startIcon': { marginRight: '7px' } }}
							>
								Book appointment
							</Button>
							<Button
								onClick={handleMessageClick}
								startIcon={<MessageIcon width={15} height={15} />}
								sx={{ height: '40px', border: '1.5px solid #E7E2F3', borderRadius: '10px', bgcolor: '#FFFFFF', color: '#2E2A40', textTransform: 'none', fontFamily: "'Outfit', sans-serif", fontWeight: 650, fontSize: '13.5px', '&:hover': { borderColor: '#7C6BD9', color: '#5F4EC2', bgcolor: '#FFFFFF' }, '& .MuiButton-startIcon': { marginRight: '7px' } }}
							>
								Message
							</Button>
							<Button
								onClick={handleSendAgreementsClick}
								startIcon={<SendIcon width={15} height={15} />}
								sx={{ height: '40px', border: '1.5px solid #E7E2F3', borderRadius: '10px', bgcolor: '#FFFFFF', color: '#2E2A40', textTransform: 'none', fontFamily: "'Outfit', sans-serif", fontWeight: 650, fontSize: '13.5px', '&:hover': { borderColor: '#7C6BD9', color: '#5F4EC2', bgcolor: '#FFFFFF' }, '& .MuiButton-startIcon': { marginRight: '7px' } }}
							>
								Send agreements
							</Button>
						</Box>

						{/* Information */}
						<Box>
							<Typography sx={{ fontFamily: "'Outfit', sans-serif", fontSize: '11.5px', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.08em', color: '#5F4EC2', mb: '12px' }}>Information</Typography>
							<Box sx={{ display: 'flex', flexDirection: 'column', gap: '11px' }}>
							{currentClient.phone_number && (
								<Box sx={{ display: 'flex', alignItems: 'flex-start', gap: '9px' }}>
									<Box sx={{ color: '#7C6BD9', flexShrink: 0, mt: '1px', display: 'flex' }}>
										<svg width="15" height="15" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round" xmlns="http://www.w3.org/2000/svg"><path d="M14.7 11.3v2a1.3 1.3 0 01-1.5 1.3A13 13 0 013.5 8.6 13 13 0 011.4 2.8 1.3 1.3 0 012.7 1.3h2a1.3 1.3 0 011.3 1.1c.1.7.3 1.3.5 1.9a1.3 1.3 0 01-.3 1.4l-.9.8a10.7 10.7 0 004 4l.8-.9a1.3 1.3 0 011.4-.3c.6.2 1.2.4 1.9.5a1.3 1.3 0 011.1 1.4z" /></svg>
									</Box>
									<Typography sx={{ fontFamily: "'Outfit', sans-serif", fontSize: '13px', fontWeight: 500, color: '#2E2A40' }}>{currentClient.phone_number}</Typography>
								</Box>
							)}
							{currentClient.email && (
								<Box sx={{ display: 'flex', alignItems: 'flex-start', gap: '9px' }}>
									<Box sx={{ color: '#7C6BD9', flexShrink: 0, mt: '1px', display: 'flex' }}>
										<svg width="15" height="15" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round" xmlns="http://www.w3.org/2000/svg"><rect x="1.6" y="3.2" width="12.8" height="9.6" rx="1.6" /><path d="M2.2 4.2L8 8.6l5.8-4.4" /></svg>
									</Box>
									<Typography sx={{ fontFamily: "'Outfit', sans-serif", fontSize: '13px', fontWeight: 500, color: '#2E2A40', overflowWrap: 'anywhere' }}>{currentClient.email}</Typography>
								</Box>
							)}
							{currentClient.address && (
								<Box sx={{ display: 'flex', alignItems: 'flex-start', gap: '9px' }}>
									<Box sx={{ color: '#7C6BD9', flexShrink: 0, mt: '1px', display: 'flex' }}>
										<svg width="15" height="15" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round" xmlns="http://www.w3.org/2000/svg"><path d="M8 14.5s4.7-4.2 4.7-7.6A4.7 4.7 0 008 2.2a4.7 4.7 0 00-4.7 4.7C3.3 10.3 8 14.5 8 14.5z" /><circle cx="8" cy="6.9" r="1.7" /></svg>
									</Box>
									<Typography sx={{ fontFamily: "'Outfit', sans-serif", fontSize: '13px', fontWeight: 500, color: '#2E2A40', flex: 1 }}>{currentClient.address}</Typography>
								</Box>
							)}
							</Box>
						</Box>

						{/* Next appointment */}
						<Box>
							<Typography sx={{ fontFamily: "'Outfit', sans-serif", fontSize: '11.5px', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.08em', color: '#5F4EC2', mb: '12px' }}>Next appointment</Typography>
							{nextAppointment ? (
								<Box sx={{ position: 'relative', overflow: 'hidden', border: '1px solid #E7E2F3', borderRadius: '12px', bgcolor: '#FFFFFF', padding: '11px 13px 11px 16px' }}>
									<Box sx={{ position: 'absolute', left: 0, top: 0, bottom: 0, width: '4px', bgcolor: '#3E9BD0' }} />
									<Typography sx={{ fontFamily: "'Outfit', sans-serif", fontSize: '14px', fontWeight: 750, color: '#2E2A40', letterSpacing: '-0.01em' }}>
										{dayjs(nextAppointment.start_ts).tz(merchant?.timezone || 'UTC').format('dddd, MMM D')}
									</Typography>
									<Typography sx={{ fontFamily: "'Outfit', sans-serif", fontSize: '12.5px', fontWeight: 600, color: '#6E6886', mt: '1px' }}>
										{formatAppointmentTime(nextAppointment.start_ts, nextAppointment.end_ts)}
									</Typography>
									<Typography sx={{ fontFamily: "'Outfit', sans-serif", fontSize: '13px', fontWeight: 600, color: '#6E6886', mt: '8px' }}>
										{nextAppointment.pets?.name || 'Pet'} · {nextAppointment.service_types?.name || 'Service'}
									</Typography>
									<NextApptAddOns appointment={nextAppointment} />
									{nextAppointment.employees && (
										<Box sx={{ display: 'flex', alignItems: 'center', gap: '7px', mt: '8px', pt: '9px', borderTop: '1px solid #F5F3FB', color: '#8B85A4' }}>
											<UserIcon width={12} height={12} />
											<Typography sx={{ fontFamily: "'Outfit', sans-serif", fontSize: '12px', fontWeight: 600, color: '#8B85A4' }}>
												{`${nextAppointment.employees.first_name} ${nextAppointment.employees.last_name}`}
											</Typography>
										</Box>
									)}
								</Box>
							) : (
								<Typography sx={{ fontFamily: "'Outfit', sans-serif", fontSize: '13px', fontWeight: 500, color: '#8B85A4' }}>No upcoming appointments</Typography>
							)}
						</Box>
					</Box>
					)}

					{/* Right Section - Pets Tab (Option B: 2-up cards + header add button) */}
					<Box sx={{ display: 'flex', flexDirection: 'column', alignSelf: 'stretch', gap: '18px', flex: 1, minWidth: 0 }}>
						{(isAddingPet || editingPet) ? (
							<AddEditPetPage
								pet={editingPet}
								onSave={handleSavePet}
								onClose={handleClosePetForm}
								onDelete={editingPet ? handleDeletePet : null}
								isMobile={false}
								merchantId={merchant?.id}
								clientId={client?.id}
								onAvatarUploaded={handleAvatarUploaded}
							/>
						) : (
							<>
								{/* Pets label */}
								<Typography sx={{ fontFamily: "'Outfit', sans-serif", fontSize: '15px', fontWeight: 750, color: '#2E2A40' }}>
									Pets{pets.length ? ` (${pets.length})` : ''}
								</Typography>

								{pets.length > 0 ? (
									<Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(2, minmax(0, 1fr))', gridAutoRows: '1fr', gap: '16px', alignItems: 'stretch' }}>
										{pets.map((pet) => (
											<Box
												key={pet.id}
												ref={pet.id === focusPetId ? focusRef : undefined}
												sx={{
													borderRadius: '16px', transition: 'box-shadow .25s',
													boxShadow: ringPet === pet.id ? `0 0 0 3px ${CMD.primary}` : 'none',
												}}
											>
												<PetInfoCard
													fullCard={true}
													pet={pet}
													onEdit={handleEditPet}
													onDelete={handleDeletePet}
													isMobile={false}
												/>
											</Box>
										))}
									</Box>
								) : (
									<Box sx={{ bgcolor: '#FFFFFF', border: '1px solid #E7E2F3', borderRadius: '14px', p: '28px 16px', textAlign: 'center', color: '#8B85A4', fontFamily: "'Outfit', sans-serif", fontWeight: 600, fontSize: '14px', boxShadow: '0 1px 3px rgba(46, 42, 64, 0.04)' }}>
										No pets yet. Add this client&rsquo;s first pet.
									</Box>
								)}
							</>
						)}
					</Box>
				</Box>

				{/* Book appointment — desktop right-hand slide-over, per the design's
				    "Client Details · booking drawer". The appointment panel is
				    otherwise a bare card meant to sit beside the calendar, so the
				    drawer supplies the surface, scrim and slide-in here. */}
				<Drawer
					anchor="right"
					open={appointmentDialogOpen}
					onClose={() => setAppointmentDialogOpen(false)}
					PaperProps={{
						sx: {
							width: 430,
							maxWidth: '100%',
							bgcolor: '#FDFCFF',
							borderLeft: `1px solid ${CMD.border}`,
							boxShadow: '-22px 0 60px rgba(46, 42, 64, 0.20)',
							backgroundImage: 'none'
						}
					}}
					slotProps={{
						backdrop: {
							sx: {
								bgcolor: 'rgba(46, 42, 64, 0.30)',
								backdropFilter: 'blur(1.5px)'
							}
						}
					}}
				>
					<AddAppointmentDialog
						open={appointmentDialogOpen}
						onClose={() => setAppointmentDialogOpen(false)}
						merchant={merchant}
						onAppointmentCreated={handleAppointmentCreated}
						prefilledClient={currentClient}
						desktopVariant="drawer"
					/>
				</Drawer>

				{/* Edit Client Dialog */}
				<AddEditClientDialog
					open={editClientDialogOpen}
					onClose={() => setEditClientDialogOpen(false)}
					merchant={merchant}
					editingClient={currentClient}
					onClientSaved={handleClientSaved}
				/>

				{/* Send Agreements Popover */}
				<Popover
					open={Boolean(sendAgreementsAnchor)}
					anchorEl={sendAgreementsAnchor}
					onClose={handleSendAgreementsClose}
					anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
					transformOrigin={{ vertical: 'top', horizontal: 'left' }}
					PaperProps={{
						sx: {
							borderRadius: '12px',
							boxShadow: '0px 16px 32px -8px rgba(12,12,13,0.4)',
							width: 267,
							overflow: 'hidden',
							mt: '6px'
						}
					}}
				>
					<Box sx={{ p: '14px 18px', display: 'flex', flexDirection: 'column', gap: '21px' }}>
						<Box sx={{
							maxHeight: agreements.length > 4 ? 4 * 44 : 'none',
							overflowY: agreements.length > 4 ? 'auto' : 'visible',
							display: 'flex',
							flexDirection: 'column'
						}}>
							{agreements.length === 0 ? (
								<Typography sx={{
									fontFamily: 'Inter',
									fontWeight: 400,
									fontSize: '13px',
									color: 'rgba(0,0,0,0.5)',
									p: '10px 8px'
								}}>
									No agreements found
								</Typography>
							) : agreements.map(ag => {
								const selected = selectedAgreementIds.includes(ag.id)
								return (
									<Box
										key={ag.id}
										onClick={() => toggleAgreementSelection(ag.id)}
										sx={{
											display: 'flex',
											justifyContent: 'center',
											alignItems: 'center',
											alignSelf: 'stretch',
											gap: '20px',
											padding: '10px 8px',
											borderRadius: '7px',
											cursor: 'pointer',
											bgcolor: selected ? 'rgba(0,0,0,0.5)' : 'transparent',
											'&:hover': { bgcolor: selected ? 'rgba(0,0,0,0.55)' : 'rgba(0,0,0,0.05)' }
										}}
									>
										<Typography sx={{
											fontFamily: 'Inter',
											fontWeight: 500,
											fontSize: '12px',
											lineHeight: '1.21em',
											color: selected ? '#FFFFFF' : 'rgba(0,0,0,0.7)',
											flex: 1,
											overflow: 'hidden',
											textOverflow: 'ellipsis',
											whiteSpace: 'nowrap'
										}}>
											{ag.title}
										</Typography>
									</Box>
								)
							})}
						</Box>
						<Button
							onClick={handleSendAgreement}
							disabled={selectedAgreementIds.length === 0 || sendingAgreement}
							sx={{
								display: 'flex',
								justifyContent: 'center',
								alignItems: 'center',
								alignSelf: 'stretch',
								gap: '10px',
								padding: '10px 20px',
								bgcolor: '#C85AFF',
								border: '1px solid #9F7AEA',
								borderRadius: '24px',
								textTransform: 'none',
								fontFamily: 'Inter',
								fontWeight: 600,
								fontSize: '12px',
								lineHeight: '1.21em',
								color: '#FFFFFF',
								'&:hover': { bgcolor: '#b84deb' },
								'&.Mui-disabled': { bgcolor: '#C85AFF', opacity: 0.5, color: '#FFFFFF' }
							}}
						>
							{sendingAgreement ? <CircularProgress size={14} sx={{ color: '#FFFFFF' }} /> : 'Send agreement'}
						</Button>
					</Box>
				</Popover>

				{/* Send success snackbar */}
				<Snackbar
					open={sendSuccessOpen}
					autoHideDuration={3000}
					onClose={() => setSendSuccessOpen(false)}
					anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
				>
					<Alert
						onClose={() => setSendSuccessOpen(false)}
						severity="success"
						sx={{ fontFamily: 'Inter', fontSize: '14px' }}
					>
						Agreement sent successfully
					</Alert>
				</Snackbar>
			</Box>
		)
	}

	// Mobile version — Nami "Client Details" mobile screen. Rendered as a
	// full-screen fixed overlay so the app top bar is hidden while browsing a
	// client, matching the staff management sub-pages. Kept below MUI's drawer
	// (1200) and modal (1300) layers so the bottom sheets and dialogs opened
	// from this screen (edit client, send agreements, …) stack above it.
	return (
		<Box sx={{
			position: 'fixed',
			inset: 0,
			zIndex: 1100,
			display: 'flex',
			flexDirection: 'column',
			bgcolor: CMD.field,
			overflow: 'hidden'
		}}>
			{/* Subheader */}
			<Box sx={{
				flex: 'none',
				display: 'flex',
				alignItems: 'center',
				gap: '11px',
				padding: '10px 16px',
				bgcolor: CMD.field,
				borderBottom: `1px solid ${CMD.border}`
			}}>
				<IconButton
					onClick={onClose}
					aria-label="Back to clients"
					sx={{ width: 40, height: 40, ml: '-8px', borderRadius: '11px', color: CMD.text, '&:hover': { bgcolor: CMD.surface } }}
				>
					<ArrowBackIcon sx={{ fontSize: 18 }} />
				</IconButton>
				<Typography sx={{
					flex: 1,
					minWidth: 0,
					fontFamily: FONTD,
					fontSize: '19px',
					fontWeight: 800,
					letterSpacing: '-0.015em',
					color: CMD.text,
					whiteSpace: 'nowrap',
					overflow: 'hidden',
					textOverflow: 'ellipsis'
				}}>
					Client Details
				</Typography>
				<IconButton
					onClick={handleEditClient}
					aria-label="Edit client"
					sx={{ width: 40, height: 40, border: `1px solid ${CMD.border}`, borderRadius: '11px', bgcolor: CMD.field, color: CMD.muted, '&:hover': { color: CMD.primaryDeep, borderColor: CMD.primary } }}
				>
					<svg width="15" height="15" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" xmlns="http://www.w3.org/2000/svg"><path d="M11.5 2.1a1.6 1.6 0 012.3 2.3l-8.2 8.2L2.2 13.8l1.2-3.4 8.1-8.3z" /></svg>
				</IconButton>
			</Box>

			{/* Body */}
			<Box sx={{
				flex: 1,
				minHeight: 0,
				overflowY: 'auto',
				display: 'flex',
				flexDirection: 'column',
				gap: '14px',
				padding: '16px 16px 24px'
			}}>
				{/* Identity */}
				<Box sx={{ flexShrink: 0, display: 'flex', flexDirection: 'column', gap: '2px' }}>
					<Box sx={{ display: 'flex', alignItems: 'center', gap: '8px', minWidth: 0 }}>
						<Typography sx={{ fontFamily: FONTD, fontSize: '21px', fontWeight: 800, letterSpacing: '-0.02em', color: CMD.text, minWidth: 0, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
							{getFullName(currentClient)}
						</Typography>
						{isNewClient && (
							<Box sx={{ flexShrink: 0, fontFamily: FONTD, fontSize: '10px', fontWeight: 700, letterSpacing: '0.04em', textTransform: 'uppercase', color: CMD.primaryDeep, bgcolor: CMD.primarySoft, padding: '2px 7px', borderRadius: '99px' }}>New</Box>
						)}
					</Box>
					<Typography sx={{ fontFamily: FONTD, fontSize: '12.5px', fontWeight: 600, color: CMD.muted }}>
						Client since {formatClientSince(currentClient.created_at)}
					</Typography>
				</Box>

				{/* Actions */}
				<Box sx={{ flexShrink: 0, display: 'flex', flexDirection: 'column', gap: '8px' }}>
					<Button
						onClick={handleBookAppointment}
						startIcon={<CalendarIcon width={16} height={16} />}
						sx={{ height: '46px', border: 'none', borderRadius: '12px', bgcolor: CMD.primary, color: '#FFFFFF', textTransform: 'none', fontFamily: FONTD, fontWeight: 700, fontSize: '14px', boxShadow: `0 2px 8px ${CMD.primarySoft}`, '&:hover': { bgcolor: CMD.primaryDeep }, '& .MuiButton-startIcon': { marginRight: '7px' } }}
					>
						Book appointment
					</Button>
					<Box sx={{ display: 'flex', gap: '8px' }}>
						<Button
							onClick={handleMessageClick}
							startIcon={<MessageIcon width={15} height={15} />}
							sx={{ flex: 1, height: '46px', border: `1.5px solid ${CMD.border}`, borderRadius: '12px', bgcolor: CMD.field, color: CMD.text, textTransform: 'none', fontFamily: FONTD, fontWeight: 650, fontSize: '14px', '&:hover': { borderColor: CMD.primary, color: CMD.primaryDeep, bgcolor: CMD.field }, '& .MuiButton-startIcon': { marginRight: '6px' } }}
						>
							Message
						</Button>
						<Button
							onClick={handleSendAgreementsClick}
							startIcon={<SendIcon width={15} height={15} />}
							sx={{ flex: 1, height: '46px', border: `1.5px solid ${CMD.border}`, borderRadius: '12px', bgcolor: CMD.field, color: CMD.text, textTransform: 'none', fontFamily: FONTD, fontWeight: 650, fontSize: '14px', '&:hover': { borderColor: CMD.primary, color: CMD.primaryDeep, bgcolor: CMD.field }, '& .MuiButton-startIcon': { marginRight: '6px' } }}
						>
							Agreements
						</Button>
					</Box>
				</Box>

				{/* Tabs */}
				<Box role="tablist" sx={{ flexShrink: 0, display: 'flex', gap: '3px', bgcolor: CMD.field, border: `1px solid ${CMD.border}`, borderRadius: '12px', padding: '4px', height: '47px', boxSizing: 'border-box' }}>
					{[['information', 'Overview'], ['pets', 'Pets']].map(([id, label]) => (
						<Button
							key={id}
							role="tab"
							aria-selected={activeTab === id}
							onClick={() => setActiveTab(id)}
							sx={{
								flex: 1,
								height: '100%',
								minHeight: '0 !important',
								display: 'inline-flex',
								alignItems: 'center',
								justifyContent: 'center',
								gap: '6px',
								border: 'none',
								borderRadius: '9px',
								padding: '0 !important',
								bgcolor: activeTab === id ? CMD.primarySoft : 'transparent',
								color: activeTab === id ? CMD.primaryDeep : CMD.muted,
								textTransform: 'none',
								fontFamily: FONTD,
								fontWeight: activeTab === id ? 750 : 650,
								fontSize: '13.5px',
								'&:hover': { bgcolor: activeTab === id ? CMD.primarySoft : CMD.surface }
							}}
						>
							{label}
							{id === 'pets' && pets.length > 0 && (
								<Box component="span" sx={{ minWidth: '19px', height: '19px', padding: '0 5px', borderRadius: '99px', bgcolor: activeTab === id ? CMD.primary : '#E7E2F3', color: activeTab === id ? '#FFFFFF' : '#6E6886', fontSize: '11px', fontWeight: 750, display: 'inline-flex', alignItems: 'center', justifyContent: 'center' }}>
									{pets.length}
								</Box>
							)}
						</Button>
					))}
				</Box>

				{/* Overview tab */}
				{activeTab === 'information' && (
					<>
						{/* Stats strip */}
						<Box sx={{ display: 'flex', alignItems: 'center', bgcolor: CMD.surface, borderRadius: '13px', padding: '11px 4px' }}>
							{[
								{ num: totalBookings, label: 'Total bookings' },
								{ num: completedBookings, label: 'Completed' }
							].map((s, i) => (
								<Box key={s.label} sx={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '1px', borderLeft: i === 1 ? `1px solid ${CMD.border}` : 'none' }}>
									<Typography sx={{ fontFamily: FONTD, fontSize: '17px', fontWeight: 800, letterSpacing: '-0.02em', color: CMD.text }}>{s.num}</Typography>
									<Typography sx={{ fontFamily: FONTD, fontSize: '11px', fontWeight: 600, color: CMD.muted }}>{s.label}</Typography>
								</Box>
							))}
						</Box>

						{/* Information panel */}
						<Box sx={{ bgcolor: CMD.field, border: `1px solid ${CMD.border}`, borderRadius: '16px', padding: '15px 15px 16px', boxShadow: '0 1px 3px rgba(46, 42, 64, 0.04)' }}>
							<Typography sx={{ fontFamily: FONTD, fontSize: '11.5px', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.08em', color: CMD.primaryDeep, mb: '10px' }}>Information</Typography>
							<Box sx={{ display: 'flex', flexDirection: 'column', gap: '11px' }}>
								{currentClient.phone_number && (
									<Box sx={{ display: 'flex', alignItems: 'flex-start', gap: '9px' }}>
										<Box sx={{ color: CMD.primary, flexShrink: 0, mt: '1px', display: 'flex' }}>
											<svg width="15" height="15" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round" xmlns="http://www.w3.org/2000/svg"><path d="M14.7 11.3v2a1.3 1.3 0 01-1.5 1.3A13 13 0 013.5 8.6 13 13 0 011.4 2.8 1.3 1.3 0 012.7 1.3h2a1.3 1.3 0 011.3 1.1c.1.7.3 1.3.5 1.9a1.3 1.3 0 01-.3 1.4l-.9.8a10.7 10.7 0 004 4l.8-.9a1.3 1.3 0 011.4-.3c.6.2 1.2.4 1.9.5a1.3 1.3 0 011.1 1.4z" /></svg>
										</Box>
										<Typography sx={{ fontFamily: FONTD, fontSize: '13px', fontWeight: 500, color: CMD.text }}>{currentClient.phone_number}</Typography>
									</Box>
								)}
								{currentClient.email && (
									<Box sx={{ display: 'flex', alignItems: 'flex-start', gap: '9px' }}>
										<Box sx={{ color: CMD.primary, flexShrink: 0, mt: '1px', display: 'flex' }}>
											<svg width="15" height="15" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round" xmlns="http://www.w3.org/2000/svg"><rect x="1.6" y="3.2" width="12.8" height="9.6" rx="1.6" /><path d="M2.2 4.2L8 8.6l5.8-4.4" /></svg>
										</Box>
										<Typography sx={{ fontFamily: FONTD, fontSize: '13px', fontWeight: 500, color: CMD.text, overflowWrap: 'anywhere' }}>{currentClient.email}</Typography>
									</Box>
								)}
								{currentClient.address && (
									<Box sx={{ display: 'flex', alignItems: 'flex-start', gap: '9px' }}>
										<Box sx={{ color: CMD.primary, flexShrink: 0, mt: '1px', display: 'flex' }}>
											<svg width="15" height="15" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round" xmlns="http://www.w3.org/2000/svg"><path d="M8 14.5s4.7-4.2 4.7-7.6A4.7 4.7 0 008 2.2a4.7 4.7 0 00-4.7 4.7C3.3 10.3 8 14.5 8 14.5z" /><circle cx="8" cy="6.9" r="1.7" /></svg>
										</Box>
										<Typography sx={{ fontFamily: FONTD, fontSize: '13px', fontWeight: 500, color: CMD.text, flex: 1 }}>{currentClient.address}</Typography>
									</Box>
								)}
							</Box>
						</Box>

						{/* Next appointment panel */}
						<Box sx={{ bgcolor: CMD.field, border: `1px solid ${CMD.border}`, borderRadius: '16px', padding: '15px 15px 16px', boxShadow: '0 1px 3px rgba(46, 42, 64, 0.04)' }}>
							<Typography sx={{ fontFamily: FONTD, fontSize: '11.5px', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.08em', color: CMD.primaryDeep, mb: '10px' }}>Next appointment</Typography>
							{nextAppointment ? (
								<Box sx={{ position: 'relative', overflow: 'hidden', border: `1px solid ${CMD.border}`, borderRadius: '12px', bgcolor: CMD.field, padding: '11px 13px 11px 16px' }}>
									<Box sx={{ position: 'absolute', left: 0, top: 0, bottom: 0, width: '4px', bgcolor: '#3E9BD0' }} />
									<Typography sx={{ fontFamily: FONTD, fontSize: '14px', fontWeight: 750, color: CMD.text, letterSpacing: '-0.01em' }}>
										{dayjs(nextAppointment.start_ts).tz(merchant?.timezone || 'UTC').format('dddd, MMM D')}
									</Typography>
									<Typography sx={{ fontFamily: FONTD, fontSize: '12.5px', fontWeight: 600, color: '#6E6886', mt: '1px' }}>
										{formatAppointmentTime(nextAppointment.start_ts, nextAppointment.end_ts)}
									</Typography>
									<Typography sx={{ fontFamily: FONTD, fontSize: '13px', fontWeight: 600, color: '#6E6886', mt: '8px' }}>
										{nextAppointment.pets?.name || 'Pet'} · {nextAppointment.service_types?.name || 'Service'}
									</Typography>
									<NextApptAddOns appointment={nextAppointment} />
									{nextAppointment.employees && (
										<Box sx={{ display: 'flex', alignItems: 'center', gap: '7px', mt: '8px', pt: '9px', borderTop: `1px solid ${CMD.surface}`, color: CMD.muted }}>
											<UserIcon width={12} height={12} />
											<Typography sx={{ fontFamily: FONTD, fontSize: '12px', fontWeight: 600, color: CMD.muted }}>
												{`${nextAppointment.employees.first_name} ${nextAppointment.employees.last_name}`}
											</Typography>
										</Box>
									)}
								</Box>
							) : (
								<Typography sx={{ fontFamily: FONTD, fontSize: '13px', fontWeight: 500, color: CMD.muted }}>No upcoming appointments</Typography>
							)}
						</Box>
					</>
				)}

				{/* Pets tab */}
				{activeTab === 'pets' && (
					<Box sx={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
						{pets.length > 0 ? (
							pets.map((pet) => (
								<Box
									key={pet.id}
									ref={pet.id === focusPetId ? focusRef : undefined}
									sx={{
										borderRadius: '16px', transition: 'box-shadow .25s',
										boxShadow: ringPet === pet.id ? `0 0 0 3px ${CMD.primary}` : 'none',
									}}
								>
									<PetInfoCard
										fullCard={true}
										pet={pet}
										onEdit={handleEditPet}
										onDelete={handleDeletePet}
										isMobile={true}
									/>
								</Box>
							))
						) : (
							<Box sx={{ bgcolor: CMD.field, border: `1px solid ${CMD.border}`, borderRadius: '16px', p: '28px 16px', textAlign: 'center', color: CMD.muted, fontFamily: FONTD, fontWeight: 600, fontSize: '14px', boxShadow: '0 1px 3px rgba(46, 42, 64, 0.04)' }}>
								No pets yet. Add this client&rsquo;s first pet.
							</Box>
						)}
						<Button
							onClick={handleAddPet}
							startIcon={<AddIcon sx={{ fontSize: 18 }} />}
							sx={{
								minHeight: '56px',
								border: '1.5px dashed #D6CFEA',
								borderRadius: '16px',
								bgcolor: 'transparent',
								color: CMD.primaryDeep,
								textTransform: 'none',
								fontFamily: FONTD,
								fontWeight: 700,
								fontSize: '14px',
								'&:hover': { bgcolor: CMD.primarySoft },
								'& .MuiButton-startIcon': { marginRight: '9px' }
							}}
						>
							Add pet
						</Button>
					</Box>
				)}
			</Box>

			{/* Mobile Add/Edit Pet full-screen */}
			{isAddingPet && (
				<AddEditPetPage
					pet={editingPet}
					onSave={handleSavePet}
					onClose={handleClosePetForm}
					onDelete={editingPet ? handleDeletePet : null}
					isMobile={true}
					merchantId={merchant?.id}
					clientId={client?.id}
					onAvatarUploaded={handleAvatarUploaded}
				/>
			)}

			{/* Add Appointment Dialog */}
			<AddAppointmentDialog
				open={appointmentDialogOpen}
				onClose={() => setAppointmentDialogOpen(false)}
				merchant={merchant}
				onAppointmentCreated={handleAppointmentCreated}
				prefilledClient={currentClient}
			/>

			{/* Edit Client Dialog */}
			<AddEditClientDialog
				open={editClientDialogOpen}
				onClose={() => setEditClientDialogOpen(false)}
				merchant={merchant}
				editingClient={currentClient}
				onClientSaved={handleClientSaved}
			/>

			{/* Send Agreements — bottom sheet */}
			<Drawer
				anchor="bottom"
				open={Boolean(sendAgreementsAnchor)}
				onClose={handleSendAgreementsClose}
				PaperProps={{
					sx: {
						bgcolor: CMD.surface,
						borderRadius: '22px 22px 0 0',
						boxShadow: '0 -12px 40px rgba(46, 42, 64, 0.24)',
						maxHeight: '88%',
						padding: '8px 16px 22px'
					}
				}}
			>
				<Box sx={{ width: '40px', height: '4px', borderRadius: '99px', bgcolor: '#DCD6EB', margin: '4px auto 12px' }} />
				<Typography sx={{ fontFamily: FONTD, fontSize: '17px', fontWeight: 800, letterSpacing: '-0.015em', color: CMD.text, mb: '12px' }}>
					Send agreements
				</Typography>
				<Box sx={{ display: 'flex', flexDirection: 'column', gap: '8px', overflowY: 'auto' }}>
					{agreements.length === 0 ? (
						<Typography sx={{ fontFamily: FONTD, fontSize: '13px', color: CMD.muted, p: '10px 8px' }}>
							No agreements found
						</Typography>
					) : agreements.map(ag => {
						const selected = selectedAgreementIds.includes(ag.id)
						return (
							<Box
								key={ag.id}
								onClick={() => toggleAgreementSelection(ag.id)}
								sx={{
									display: 'flex',
									alignItems: 'center',
									gap: '11px',
									textAlign: 'left',
									padding: '12px 13px',
									borderRadius: '13px',
									cursor: 'pointer',
									bgcolor: selected ? '#F7F5FC' : CMD.field,
									border: selected ? '1.5px solid #D8CEF2' : '1.5px solid #EAE6F3',
									minHeight: '52px'
								}}
							>
								<Box sx={{ width: '22px', height: '22px', flexShrink: 0, borderRadius: '7px', display: 'flex', alignItems: 'center', justifyContent: 'center', bgcolor: selected ? CMD.primary : '#FFFFFF', border: selected ? `1.5px solid ${CMD.primary}` : '1.5px solid #D4CDE6', color: '#FFFFFF' }}>
									{selected && <svg width="12" height="12" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><path d="M3 8.5l3.2 3L13 4.5" /></svg>}
								</Box>
								<Typography sx={{ fontFamily: FONTD, fontSize: '14px', fontWeight: 700, color: CMD.text }}>
									{ag.title}
								</Typography>
							</Box>
						)
					})}
				</Box>
				<Button
					onClick={handleSendAgreement}
					disabled={selectedAgreementIds.length === 0 || sendingAgreement}
					sx={{
						mt: '14px',
						width: '100%',
						height: '46px',
						border: 'none',
						borderRadius: '12px',
						bgcolor: CMD.primary,
						color: '#FFFFFF',
						textTransform: 'none',
						fontFamily: FONTD,
						fontWeight: 700,
						fontSize: '14px',
						'&:hover': { bgcolor: CMD.primaryDeep },
						'&.Mui-disabled': { bgcolor: CMD.primary, opacity: 0.45, color: '#FFFFFF' }
					}}
				>
					{sendingAgreement ? <CircularProgress size={16} sx={{ color: '#FFFFFF' }} /> : `Send${selectedAgreementIds.length ? ' ' + selectedAgreementIds.length : ''} agreement${selectedAgreementIds.length === 1 ? '' : 's'}`}
				</Button>
			</Drawer>

			{/* Send success snackbar */}
			<Snackbar
				open={sendSuccessOpen}
				autoHideDuration={3000}
				onClose={() => setSendSuccessOpen(false)}
				anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
			>
				<Alert
					onClose={() => setSendSuccessOpen(false)}
					severity="success"
					sx={{ fontFamily: 'Inter', fontSize: '14px' }}
				>
					Agreement sent successfully
				</Alert>
			</Snackbar>
		</Box>
	)
}

export default ClientDetailsDialog
